import React from 'react';
import PropTypes from 'prop-types';

import utils from '../utils';
// Import standard block components
import Line from './blocks/Line';
import Rectangle from './blocks/Rectangle';
import Circle from './blocks/Circle';
import GroupStroke from './blocks/GroupStroke';
import Path from './blocks/Path';
import Text from './blocks/Text';
import PathGroup from './blocks/PathGroup';
import Group from './blocks/Group';
import Symbol from './blocks/Symbol';
import Image from './blocks/Image';
import Video from './blocks/Video';
import Iframe from './blocks/Iframe';
import Lottie from './blocks/Lottie';
import Code from './blocks/Code';
import Chart from './blocks/Chart';

// Import rich block components
import Framer from './blocks/rich/Framer';
import Sketchfab from './blocks/rich/Sketchfab';
import Giphy from './blocks/rich/Giphy';
import Invision from './blocks/rich/Invision';
import Airtable from './blocks/rich/Airtable';
import Codepen from './blocks/rich/Codepen';
import SoundCloud from './blocks/rich/SoundCloud';
import Typeform from './blocks/rich/Typeform';
import Figma from './blocks/rich/Figma';
import Marvel from './blocks/rich/Marvel';
import Protoio from './blocks/rich/Protoio';
import GoogleMaps from './blocks/rich/GoogleMaps';
import GoogleStreetView from './blocks/rich/GoogleStreetView';
import Xd from './blocks/rich/Xd';
import BrushPath from './blocks/BrushPath';

const Block = props => {
  const [transitionStatus, setTransitionStatus] = React.useState(
    props.transitionStatus,
  );

  React.useEffect(() => {
    setTransitionStatus(props.transitionStatus);
  }, [props.transitionStatus]);

  const getHyperlink = linkString => {
    if (!linkString) {
      return null;
    }

    if (linkString.toLowerCase() === 'previous') {
      return `#${Math.max(props.currentSlideNumber - 1, 1)}`;
    }
    if (linkString.toLowerCase() === 'next') {
      return `#${props.currentSlideNumber + 1}`;
    }

    return linkString;
  };

  /**
   * Create common styles for sub-block components.
   * Each component will add its own style properties, the code below provides common
   * style properties, mainly position & dimensions.
   *
   * The applyScaleFix is a boolean flag that should be set to true for embeds.
   * It will multiply the block width and height by the global scale, and perform an inverse
   * scale transformation, so that the embedded content elements have the correct size
   * (youtube player controls is a good example)
   *
   * @param {boolean} applyScaleFix
   * @returns {*}
   */

  /**
   * Create props to pass to sub-block components
   *
   * @returns {*}
   */
  const createBlockProps = (applyScaleFix = false, calcDimensions = false) => {
    const params = props.params;
    // manage default basic params
    params.scaleX = params.scaleX || 1;
    params.scaleY = params.scaleY || 1;
    params.opacity =
      params.opacity || params.opacity === 0 ? params.opacity : 1;
    params.angle = params.angle || 0;

    params.globalCompositeOperation =
      params.globalCompositeOperation || 'source-over';
    // / TO DO DEFINE DEFAULT VALUES IN PARAM.

    return {
      style: utils.buildBlockStyles(
        params,
        props.globalScale,
        applyScaleFix,
        calcDimensions,
      ),
      hyperlink: props.linksEnabled
        ? getHyperlink(params.ludus.hyperlink)
        : null,
      linksEnabled: props.linksEnabled,
      params,
      matrixGroup: props.matrixGroup,
      transitionStatus,
      applyScaleFix,
      appInstanceId: props.appInstanceId,
      globalScale: props.globalScale,
      windowHasFocus: props.windowHasFocus,
      groupAngle: params.angle + props.groupAngle || 0,
      groupOpacity: params.opacity * props.groupOpacity || 1,
      groupScaleX: 1, // params.scaleX * props.groupScaleX,
      groupScaleY: 1, // params.scaleY * props.groupScaleY,
    };
  };

  /**
   * Render a "rich" block (depending on the source)
   *
   * @returns {*}
   */
  const renderRich = () => {
    const applyScale = props.params.ludus.applyScale || false;
    switch (props.params.ludus.source) {
      case 'framer':
        return <Framer {...createBlockProps(true, true)} />;
      case 'sketchfab':
        return <Sketchfab {...createBlockProps(true, true)} />;
      case 'giphy':
        return <Giphy {...createBlockProps(false, true)} />;
      case 'invision':
        return <Invision {...createBlockProps(false, true)} />;
      case 'airtable':
        return <Airtable {...createBlockProps(true, true)} />;
      case 'codepen':
        return <Codepen {...createBlockProps(applyScale, true)} />;
      case 'soundcloud':
        return <SoundCloud {...createBlockProps(true)} />;
      case 'typeform':
        return <Typeform {...createBlockProps(true, true)} />;
      case 'figma':
        return <Figma {...createBlockProps(false, true)} />;
      case 'marvel':
        return <Marvel {...createBlockProps(false, true)} />;
      case 'protoio':
        return <Protoio {...createBlockProps(false, true)} />;
      case 'maps':
        return <GoogleMaps {...createBlockProps(true, true)} />;
      case 'streetview':
        return <GoogleStreetView {...createBlockProps(true, true)} />;
      case 'xd':
        return <Xd {...createBlockProps(true, true)} />;
      default:
        return null;
    }
  };

  const applyScale = props.params.ludus.applyScale || false;
  switch (props.type) {
    case 'group':
      return (
        <Group
          {...createBlockProps()}
          currentSlideNumber={props.currentSlideNumber}
          globalScale={props.globalScale}
        />
      );
    case 'rectangle':
    case 'ludus_rectangle':
      return (
        <Rectangle
          {...createBlockProps(false, true)}
          globalScale={props.globalScale}
        />
      );
    case 'circle':
    case 'ludus_circle':
      return <Circle {...createBlockProps(false, true)} />;
    case 'ludus_path':
    case 'path':
    case 'polygon':
      return <Path {...createBlockProps(false, true)} />;
    case 'brushPath':
      return <BrushPath {...createBlockProps(false, true)} />;
    case 'arrow':
    case 'line':
      return <Line {...createBlockProps()} />;
    case 'text':
    case 'title':
    case 'paragraph':
      return (
        <Text
          {...createBlockProps(false, true)}
          currentSlideNumber={props.currentSlideNumber}
          globalScale={props.globalScale}
          hasSymbolAsParent={props.hasSymbolAsParent}
        />
      );
    case 'image':
      return (
        <Image
          {...createBlockProps(false, true)}
          globalScale={props.globalScale}
          currentSlideNumber={props.currentSlideNumber}
        />
      );
    case 'path-group':
      return <PathGroup {...createBlockProps()} />;
    case 'video':
      return (
        <Video {...createBlockProps(false, true)} visible={props.visible} />
      );
    case 'rich':
      return renderRich();
    case 'groupStroke':
      return <GroupStroke {...createBlockProps()} />;
    case 'symbol':
      return (
        <Symbol
          {...createBlockProps()}
          currentSlideNumber={props.currentSlideNumber}
        />
      );
    case 'iframe':
      return (
        <Iframe
          {...createBlockProps(applyScale, true)}
          transitionStatus={transitionStatus}
        />
      );
    case 'lottie':
      return <Lottie {...createBlockProps(applyScale, true)} />;
    case 'code':
      return <Code {...createBlockProps()} />;
    case 'chart':
      return <Chart {...createBlockProps()} />;
    default:
      return null;
  }
};

Block.propTypes = {
  appInstanceId: PropTypes.string.isRequired,
  globalScale: PropTypes.number.isRequired,
  windowHasFocus: PropTypes.bool.isRequired,
  linksEnabled: PropTypes.bool.isRequired,
  matrixGroup: PropTypes.arrayOf(PropTypes.number),
  groupAngle: PropTypes.number.isRequired,
  groupOpacity: PropTypes.number.isRequired,
  groupScaleX: PropTypes.number.isRequired,
  groupScaleY: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  transitionStatus: PropTypes.string.isRequired,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
  currentSlideNumber: PropTypes.number.isRequired,
  hasSymbolAsParent: PropTypes.bool,
  visible: PropTypes.bool,
};
Block.defaultProps = {
  matrixGroup: null,
  hasSymbolAsParent: false,
};

export default Block;
